import { cn } from '@/lib/utils'
import { createContext, isValidElement, ReactNode, useCallback, useContext, useMemo, useState } from 'react'

declare module '@tanstack/react-table' {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData, TValue> {
    disableCellTruncate?: boolean
  }
}

type DataTableCellExpansionContextValue = {
  isExpanded: (cellId: string) => boolean
  toggle: (cellId: string) => void
}

const DataTableCellExpansionContext = createContext<DataTableCellExpansionContextValue | null>(null)

export function DataTableCellExpansionProvider({ children }: { children: ReactNode }) {
  const [expandedCells, setExpandedCells] = useState<Set<string>>(new Set())

  const toggle = useCallback((cellId: string) => {
    setExpandedCells((current) => {
      const next = new Set(current)
      if (next.has(cellId)) {
        next.delete(cellId)
      } else {
        next.add(cellId)
      }
      return next
    })
  }, [])

  const isExpanded = useCallback((cellId: string) => expandedCells.has(cellId), [expandedCells])

  const value = useMemo(() => ({ isExpanded, toggle }), [isExpanded, toggle])

  return <DataTableCellExpansionContext.Provider value={value}>{children}</DataTableCellExpansionContext.Provider>
}

function useDataTableCellExpansion() {
  const context = useContext(DataTableCellExpansionContext)
  if (!context) {
    throw new Error('useDataTableCellExpansion must be used within DataTableCellExpansionProvider')
  }
  return context
}

type TableTextCellProps = {
  cellId: string
  value: string
}

export function TableTextCell({ cellId, value }: TableTextCellProps) {
  const { isExpanded, toggle } = useDataTableCellExpansion()
  const expanded = isExpanded(cellId)

  return (
    <button
      type="button"
      className="m-0 block w-full min-w-0 max-w-full cursor-pointer border-0 bg-transparent p-0 text-left text-inherit"
      title={!expanded ? value : undefined}
      onClick={() => toggle(cellId)}
    >
      <span
        className={cn(
          'block w-full min-w-0 max-w-full',
          expanded ? 'whitespace-normal break-words' : 'overflow-hidden text-ellipsis whitespace-nowrap'
        )}
      >
        {value}
      </span>
    </button>
  )
}

type ExpandableCellContentProps = {
  cellId: string
  children: ReactNode
}

function getTextValue(children: ReactNode): string | null {
  if (children == null || children === false) return null
  if (typeof children === 'string' || typeof children === 'number') return String(children)
  if (Array.isArray(children) && children.length === 1) {
    return getTextValue(children[0])
  }
  return null
}

export function ExpandableCellContent({ cellId, children }: ExpandableCellContentProps) {
  const textValue = getTextValue(children)

  if (textValue != null) {
    return <TableTextCell cellId={cellId} value={textValue} />
  }

  if (children == null || children === false) {
    return <span className="text-muted-foreground">-</span>
  }

  if (isValidElement(children)) {
    return <>{children}</>
  }

  return <>{children}</>
}
